import { utcDay } from './utcDay';

export type HistoryPoint = { date: string; views?: number | null };
export type HistorySeriesPoint = { date: string; views: number | null; gained: number | null };

const DAY_MS = 86_400_000;

const finite = (value: number | null | undefined) => (typeof value === 'number' && Number.isFinite(value) ? value : null);

/**
 * One point per UTC day from the first to the last observation. Days without an observation stay
 * null (never 0), and a day's gain is computed only against an observed previous day.
 */
export function buildHistorySeries(points: readonly HistoryPoint[]): HistorySeriesPoint[] {
  const byDay = new Map<number, number | null>();
  points.forEach((point) => {
    const day = utcDay(point.date);
    if (day !== null) byDay.set(day, finite(point.views));
  });
  const days = [...byDay.keys()].sort((a, b) => a - b);
  if (!days.length) return [];
  const series: HistorySeriesPoint[] = [];
  for (let day = days[0]; day <= days[days.length - 1]; day += DAY_MS) {
    const views = byDay.get(day) ?? null;
    const previous = byDay.get(day - DAY_MS) ?? null;
    series.push({
      date: new Date(day).toISOString().slice(0, 10),
      views,
      gained: views !== null && previous !== null ? views - previous : null,
    });
  }
  return series;
}

/** The first tracked day when tracking began more than a day after publishing (imported posts), else null. */
export function trackingStartedAfterPublish(points: readonly HistoryPoint[], publishedAt: string | null | undefined): string | null {
  const firstDay = points.map((point) => utcDay(point.date)).filter((day): day is number => day !== null).sort((a, b) => a - b)[0];
  const published = publishedAt ? Date.parse(publishedAt) : NaN;
  if (firstDay === undefined || !Number.isFinite(published)) return null;
  const publishedDay = utcDay(new Date(published).toISOString().slice(0, 10)) as number;
  return firstDay - publishedDay > DAY_MS ? new Date(firstDay).toISOString().slice(0, 10) : null;
}

/** Indexes of observed points with no observed neighbour; a line cannot draw them, so they need a dot. */
export function isolatedPointIndexes(series: readonly HistorySeriesPoint[]): Set<number> {
  const isolated = new Set<number>();
  series.forEach((point, index) => {
    if (point.views !== null && series[index - 1]?.views == null && series[index + 1]?.views == null) isolated.add(index);
  });
  return isolated;
}
